import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ColumnDef} from "@tanstack/react-table";
import {DataTable} from "@/lib/components/DataTable";
import {CollaborationSummaryDTO} from "@/api/data-contracts";
import {ReportI18N} from "@/translations";

interface CollaborationSummaryReportProps {
    data: CollaborationSummaryDTO[];
}

interface CollaborationSummaryRow extends CollaborationSummaryDTO {
    id: number;
}

/**
 * Report table showing collaboration counts per requester-supplier company pair.
 */
const CollaborationSummaryReport: React.FC<CollaborationSummaryReportProps> = ({ data }) => {
    const { t } = useTranslation();

    const rows = useMemo<CollaborationSummaryRow[]>(
        () => data.map((item, index) => ({ ...item, id: index })),
        [data]
    );

    const columns = useMemo<ColumnDef<CollaborationSummaryRow>[]>(
        () => [
            {
                header: t(ReportI18N.page.column.requester),
                accessorFn: (row) => row.requesterCompanyName ?? "",
                id: "requesterCompanyName"
            },
            {
                header: t(ReportI18N.page.column.supplier),
                accessorFn: (row) => row.supplierCompanyName ?? "",
                id: "supplierCompanyName"
            },
            {
                header: t(ReportI18N.page.column.count),
                accessorFn: (row) => row.quoteCount ?? 0,
                id: "quoteCount"
            }
        ],
        [t]
    );

    return <DataTable columns={columns} data={rows} noResultsValue="" striped maxHeight={940} />;
};

export default CollaborationSummaryReport;
